import React, { useState, useCallback } from 'react';
import { Modal, Form, Input, Upload, Button, Checkbox, message, notification } from 'antd';
import { UploadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { hazardService } from '@iso/services/hazard.service';
import { apiEndpoint } from '@iso/lib/helpers/endpoint';
import { authHeader } from '@iso/lib/helpers/authHeader';

const { TextArea } = Input;

/**
 * Rectify Hazard Modal
 * Allows user to mark a hazard as rectified with rectification notes and photos,
 * optionally closing the hazard in the same action.
 */
const ResolveHazardModal = ({ visible, hazardId, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [attachmentIds, setAttachmentIds] = useState([]);

  const addAttachmentId = useCallback((id) => {
    if (id === null || id === undefined) {
      return;
    }
    setAttachmentIds(prev => {
      if (!prev.includes(id)) {
        return [...prev, id];
      }
      return prev;
    });
  }, []);

  const removeAttachmentId = useCallback((id) => {
    setAttachmentIds(prev => prev.filter(attachId => attachId !== id));
  }, []);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const isClosed = !!values.is_closed;

      const result = await hazardService.resolveHazard(
        hazardId,
        values.resolution_notes,
        attachmentIds,
        null,
        isClosed
      );

      if (result && result.code === '0000') {
        notification.success({
          message: 'Success',
          description: isClosed
            ? 'Hazard rectified and closed'
            : 'Hazard rectified successfully',
          icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
        });
        form.resetFields();
        setFileList([]);
        setAttachmentIds([]);
        if (onSuccess) onSuccess();
      } else {
        notification.error({
          message: 'Error',
          description: result.message || 'Failed to rectify hazard',
        });
      }
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    setAttachmentIds([]);
    if (onCancel) onCancel();
  };

  const uploadProps = {
    name: 'Attachment[file]',
    action: apiEndpoint.attachments,
    headers: authHeader(true),
    listType: 'picture',
    multiple: true,
    fileList,
    onChange: (info) => {
      setFileList(info.fileList);

      if (info.file.status === 'done') {
        message.success(`${info.file.name} uploaded successfully`);
        // Add attachment ID when upload completes
        if (info.file.response && info.file.response.attachment && info.file.response.attachment.id) {
          addAttachmentId(info.file.response.attachment.id);
        }
      } else if (info.file.status === 'error') {
        message.error(`${info.file.name} upload failed`);
      }
    },
    onRemove: (file) => {
      // Remove attachment ID when file is removed
      if (file.response && file.response.attachment && file.response.attachment.id) {
        removeAttachmentId(file.response.attachment.id);
      }
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
  };

  return (
    <Modal
      title="Rectify Hazard"
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText="Rectify"
      okButtonProps={{
        icon: <CheckCircleOutlined />,
        style: { backgroundColor: '#0B2A1B', borderColor: '#0B2A1B' }
      }}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{ is_closed: true }}
      >
        <Form.Item
          name="resolution_notes"
          label="How was the hazard rectified?"
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Please describe how the hazard was rectified'
            },
            {
              min: 10,
              message: 'Please provide at least 10 characters'
            }
          ]}
        >
          <TextArea
            rows={4}
            placeholder="Describe how the hazard was rectified..."
            maxLength={1000}
            showCount
          />
        </Form.Item>

        <Form.Item name="is_closed" valuePropName="checked">
          <Checkbox>Close this hazard</Checkbox>
        </Form.Item>

        <Form.Item
          label="Rectification Photos"
          extra="Upload photos showing the rectified hazard (optional)"
        >
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>Upload Photos</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ResolveHazardModal;
